/**
 * Unified Wagtail CMS page component.
 * Handles all page types dynamically based on the URL path.
 *
 * Custom pages can register their own components that render before CMS content.
 * Register custom pages in the CUSTOM_PAGE_REGISTRY below.
 */

import { useLocation, Link } from 'react-router-dom';
import { usePage } from '@/hooks/useCms';
import StreamField from '@/components/StreamField';
import PageHead from '@/components/PageHead';
import { PageTypeEnum } from '@/api/generated';
import type { PageTree, ChildPage } from '@/api/generated';
import { useEffect, type ReactNode } from 'react';
import CommentSection from '@/components/comments/CommentSection';

/**
 * Props passed to custom page components.
 * Custom content renders first, then CMS content via the cmsContent render prop.
 */
export interface CustomPageProps {
  page: PageTree;
  /** Render this to display the CMS body content */
  cmsContent: ReactNode;
}

/** Renders a list of child pages as links */
const ChildPageList = ({ pages }: { pages: ChildPage[] }) => {
  if (!pages.length) {
    return <p className="text-gray-600">No content yet. Check back soon!</p>;
  }

  return (
    <div className="space-y-8">
      {pages.map((child) => (
        <article key={child.slug} className="border-b border-gray-200 pb-8">
          <Link to={child.url_path} className="group">
            <h2 className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
              {child.title}
            </h2>
          </Link>
          {child.first_published_at && (
            <p className="mt-1 text-sm text-gray-500">
              {new Date(child.first_published_at).toLocaleDateString('en-AU', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          )}
          <Link
            to={child.url_path}
            className="inline-block mt-4 text-blue-600 hover:text-blue-500 font-medium"
          >
            Read more →
          </Link>
        </article>
      ))}
    </div>
  );
};

/** Blog index - custom content (child list) first, then CMS body */
const BlogIndexCustom = ({ page, cmsContent }: CustomPageProps) => {
  return (
    <div className="max-w-4xl mx-auto">
      <PageHead
        title={page.seo_title || page.title}
        description={page.search_description || undefined}
        canonicalPath={page.url_path}
      />
      <h1 className="text-4xl font-bold text-gray-900 mb-8">{page.title}</h1>

      {/* Custom content first: list of blog posts */}
      <ChildPageList pages={page.children ?? []} />

      {/* CMS body content after */}
      {cmsContent && <div className="mt-8 mb-12">{cmsContent}</div>}
    </div>
  );
};

/** Blog post with back link and date */
const BlogPostLayout = ({ page }: { page: PageTree }) => {
  const location = useLocation();
  useEffect(() => {
    window.umami?.track('blog_read', { title: page.title, path: location.pathname });
  }, [page.title, location.pathname]);

  return (
    <article className="max-w-3xl mx-auto">
      <PageHead
        title={page.seo_title || page.title}
        description={page.search_description || undefined}
        ogImage={page.og_image?.url}
        canonicalPath={page.url_path}
        ogType="article"
      />
      <Link to="/blog" className="text-blue-600 hover:text-blue-500 mb-4 inline-block">
        ← Back to blog
      </Link>

      <header className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">{page.title}</h1>
        {page.first_published_at && (
          <p className="text-gray-500">
            {new Date(page.first_published_at).toLocaleDateString('en-AU', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        )}
      </header>

      <StreamField blocks={page.body} />
      <CommentSection threadId={`blog:${page.id}`} />
    </article>
  );
};

/** Flexible page - shows content and children if any */
const FlexiblePageLayout = ({ page }: { page: PageTree }) => {
  const hasBody = page.body && page.body.length > 0;
  const hasChildren = page.children && page.children.length > 0;

  return (
    <div className="max-w-4xl mx-auto">
      <PageHead
        title={page.seo_title || page.title}
        description={page.search_description || undefined}
        canonicalPath={page.url_path}
      />
      <h1 className="text-4xl font-bold text-gray-900 mb-8">{page.title}</h1>

      {hasBody && <StreamField blocks={page.body} />}

      {hasChildren && (
        <div className={hasBody ? 'mt-12' : ''}>
          <ChildPageList pages={page.children} />
        </div>
      )}
    </div>
  );
};

/** Default layout for unknown page types */
const DefaultPageLayout = ({ page }: { page: PageTree }) => {
  return (
    <div className="max-w-3xl mx-auto">
      <PageHead
        title={page.seo_title || page.title}
        description={page.search_description || undefined}
        canonicalPath={page.url_path}
      />
      <h1 className="text-4xl font-bold text-gray-900 mb-8">{page.title}</h1>
      {page.body && page.body.length > 0 && <StreamField blocks={page.body} />}
    </div>
  );
};

/**
 * Registry of custom page components by path.
 * The component receives the CMS page data and a cmsContent render prop.
 *
 * Example:
 *   '/test': ({ page, cmsContent }) => (
 *     <div>
 *       <h1>{page.title}</h1>
 *       <MyCustomContent />
 *       {cmsContent}
 *     </div>
 *   )
 */
const CUSTOM_PAGE_REGISTRY: Record<string, React.FC<CustomPageProps>> = {
  '/blog': BlogIndexCustom,
  // Add more custom pages here:
  // '/test': TestPageCustom,
};

const WagtailPage = () => {
  const location = useLocation();
  // Remove leading slash for API call
  const path = location.pathname.replace(/^\/+/, '');

  const { data: page, loading, error } = usePage(path);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h1>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  // Check if there's a custom component for this path
  const CustomComponent = CUSTOM_PAGE_REGISTRY[location.pathname];
  if (CustomComponent) {
    const cmsContent =
      page.body && page.body.length > 0 ? <StreamField blocks={page.body} /> : null;
    return <CustomComponent page={page} cmsContent={cmsContent} />;
  }

  // Render based on page type
  switch (page.page_type) {
    case PageTypeEnum.BlogPost:
      return <BlogPostLayout page={page} />;
    case PageTypeEnum.Flexible:
      return <FlexiblePageLayout page={page} />;
    default:
      return <DefaultPageLayout page={page} />;
  }
};

export default WagtailPage;
